import type { InfiniteData, Query, QueryClient } from "@tanstack/react-query";

import type { FeedRequest } from "../api/feed";
import type { FeedPage, HomeFeedPage, PostResponse } from "../types/generated";
import { feedQueryKeys, homeFeedQueryKeys } from "./feedKeys";
import { resetFeedFreshnessTelemetry } from "./feedTelemetry";
import {
  mergePostIntoHomePages,
  pinConfirmedHomePost,
  removeConfirmedHomePostPin,
  replaceConfirmedHomePostPin,
  resetConfirmedHomePins,
  reconcileConfirmedHomePins,
  rewriteHomePages,
} from "./homeFeedCache";

export interface FeedCacheTarget {
  scope: FeedRequest["scope"] | "home";
  subject?: string;
}

interface FeedKeyDescriptor {
  scope: FeedRequest["scope"];
  subject: string | null;
}

function describeFeedKey(
  queryKey: readonly unknown[],
): FeedKeyDescriptor | null {
  if (
    queryKey[0] !== "feed" ||
    queryKey[1] !== "list" ||
    typeof queryKey[2] !== "string"
  ) {
    return null;
  }
  const scope = queryKey[2] as FeedRequest["scope"];
  if (
    !["friends", "communities", "community", "user", "discovery"].includes(
      scope,
    )
  ) {
    return null;
  }
  const subject = typeof queryKey[3] === "string" ? queryKey[3] : null;
  return { scope, subject };
}

function matchesTargets(
  query: { queryKey: readonly unknown[] },
  targets: readonly FeedCacheTarget[],
): boolean {
  const descriptor = describeFeedKey(query.queryKey);
  return (
    descriptor !== null &&
    targets.some(
      (target) =>
        target.scope === descriptor.scope &&
        (target.subject === undefined || target.subject === descriptor.subject),
    )
  );
}

function rewritePages(
  data: InfiniteData<FeedPage> | undefined,
  rewrite: (post: PostResponse) => PostResponse | null,
): InfiniteData<FeedPage> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.flatMap((post) => {
        const next = rewrite(post);
        return next ? [next] : [];
      }),
    })),
  };
}

function mergePostIntoPages(
  data: InfiniteData<FeedPage> | undefined,
  post: PostResponse,
): InfiniteData<FeedPage> | undefined {
  if (!data?.pages.length) return data;
  const withoutDuplicate = rewritePages(data, (candidate) =>
    candidate.post_id === post.post_id ? null : candidate,
  );
  if (!withoutDuplicate?.pages.length) return withoutDuplicate;
  const [first, ...rest] = withoutDuplicate.pages;
  return {
    ...withoutDuplicate,
    pages: [{ ...first, items: [post, ...first.items] }, ...rest],
  };
}

function containsPost(
  data: InfiniteData<FeedPage> | undefined,
  postId: string,
): boolean {
  return (
    data?.pages.some((page) =>
      page.items.some((post) => post.post_id === postId),
    ) ?? false
  );
}

/** Reconcile a canonical post Feed read without dropping projection-lagged writes. */
export function reconcileConfirmedFeedPins(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  data: InfiniteData<FeedPage>,
): InfiniteData<FeedPage> {
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  if (!query) return data;
  const state = confirmedPinStates.get(queryClient);
  const pinned = state?.queries.get(query.queryHash);
  if (!pinned || pinned.query !== query) return data;

  let reconciled = data;
  for (const [postId, post] of pinned.posts) {
    if (containsPost(reconciled, postId)) pinned.posts.delete(postId);
    else reconciled = mergePostIntoPages(reconciled, post) ?? reconciled;
  }
  if (pinned.posts.size === 0) state?.queries.delete(query.queryHash);
  releasePinStateIfEmpty(queryClient, state as ConfirmedPinState);
  return reconciled;
}

export { reconcileConfirmedHomePins };

interface ConfirmedQueryPins {
  query: Query;
  posts: Map<string, PostResponse>;
}

interface ConfirmedPinState {
  queries: Map<string, ConfirmedQueryPins>;
  unsubscribe: () => void;
}

const confirmedPinStates = new WeakMap<QueryClient, ConfirmedPinState>();
const queryAuthorityRevisions = new WeakMap<Query, number>();

function bumpAuthorityRevision(query: Query): void {
  queryAuthorityRevisions.set(
    query,
    (queryAuthorityRevisions.get(query) ?? 0) + 1,
  );
}

export function getFeedCacheAuthorityRevision(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): number {
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  return query ? (queryAuthorityRevisions.get(query) ?? 0) : 0;
}

function releasePinStateIfEmpty(
  queryClient: QueryClient,
  state: ConfirmedPinState,
): void {
  if (state.queries.size !== 0) return;
  state.unsubscribe();
  confirmedPinStates.delete(queryClient);
}

function ensureConfirmedPinState(queryClient: QueryClient): ConfirmedPinState {
  const existing = confirmedPinStates.get(queryClient);
  if (existing) return existing;

  const state: ConfirmedPinState = {
    queries: new Map(),
    unsubscribe: () => undefined,
  };
  state.unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    const pinned = state.queries.get(event.query.queryHash);
    if (!pinned || pinned.query !== event.query) return;

    if (event.type === "removed") {
      state.queries.delete(event.query.queryHash);
      releasePinStateIfEmpty(queryClient, state);
      return;
    }
    if (
      event.type !== "updated" ||
      event.action.type !== "success" ||
      event.action.manual
    ) {
      return;
    }

    // A successful network read is the only observation that may release a
    // server-confirmed pin. Projection-lagged reads are repaired immediately;
    // manual cache writes are ignored so they cannot certify themselves.
    let data = event.query.state.data as InfiniteData<FeedPage> | undefined;
    let repaired = false;
    for (const [postId, post] of pinned.posts) {
      if (containsPost(data, postId)) {
        pinned.posts.delete(postId);
      } else {
        data = mergePostIntoPages(data, post);
        repaired = true;
      }
    }
    if (pinned.posts.size === 0) {
      state.queries.delete(event.query.queryHash);
    }
    if (repaired) {
      queryClient.setQueryData(event.query.queryKey, data);
      void queryClient.invalidateQueries({
        queryKey: event.query.queryKey,
        exact: true,
        refetchType: "none",
      });
    }
    releasePinStateIfEmpty(queryClient, state);
  });
  confirmedPinStates.set(queryClient, state);
  return state;
}

/** Merge a server-confirmed publication without moving any cursor boundary. */
export function mergeConfirmedFeedPost(
  queryClient: QueryClient,
  post: PostResponse,
  targets: readonly FeedCacheTarget[],
): void {
  const matchingQueries = queryClient.getQueryCache().findAll({
    queryKey: feedQueryKeys.all,
    predicate: (query) => matchesTargets(query, targets),
  });
  for (const query of matchingQueries) {
    const data = query.state.data as InfiniteData<FeedPage> | undefined;
    if (!data?.pages.length) continue;
    bumpAuthorityRevision(query);
    queryClient.setQueryData(query.queryKey, mergePostIntoPages(data, post));
    const pinState = ensureConfirmedPinState(queryClient);
    const pinned = pinState.queries.get(query.queryHash);
    if (pinned?.query === query) {
      pinned.posts.delete(post.post_id);
      pinned.posts.set(post.post_id, post);
    } else {
      pinState.queries.set(query.queryHash, {
        query,
        posts: new Map([[post.post_id, post]]),
      });
    }
  }
  if (targets.some((target) => target.scope === "home")) {
    for (const query of queryClient.getQueryCache().findAll({
      queryKey: homeFeedQueryKeys.all,
    })) {
      const data = query.state.data as InfiniteData<HomeFeedPage> | undefined;
      if (!data?.pages.length) continue;
      bumpAuthorityRevision(query);
      queryClient.setQueryData(
        query.queryKey,
        mergePostIntoHomePages(data, post),
      );
      pinConfirmedHomePost(queryClient, query, post);
    }
    void queryClient.invalidateQueries({
      queryKey: homeFeedQueryKeys.all,
      refetchType: "none",
    });
  }
  void queryClient.invalidateQueries({
    queryKey: feedQueryKeys.all,
    predicate: (query) => matchesTargets(query, targets),
    refetchType: "none",
  });
}

/** Apply server-confirmed canonical edits to every already-loaded Feed card. */
export function replaceConfirmedFeedPost(
  queryClient: QueryClient,
  post: PostResponse,
): void {
  for (const query of queryClient.getQueryCache().findAll({
    queryKey: feedQueryKeys.all,
  })) {
    bumpAuthorityRevision(query);
  }
  for (const query of queryClient.getQueryCache().findAll({
    queryKey: homeFeedQueryKeys.all,
  })) {
    bumpAuthorityRevision(query);
  }
  for (const pinned of confirmedPinStates.get(queryClient)?.queries.values() ??
    []) {
    if (pinned.posts.has(post.post_id)) {
      pinned.posts.set(post.post_id, post);
    }
  }
  queryClient.setQueriesData<InfiniteData<FeedPage>>(
    { queryKey: feedQueryKeys.all },
    (data) =>
      rewritePages(data, (candidate) =>
        candidate.post_id === post.post_id ? post : candidate,
      ),
  );
  replaceConfirmedHomePostPin(queryClient, post);
  queryClient.setQueriesData<InfiniteData<HomeFeedPage>>(
    { queryKey: homeFeedQueryKeys.all },
    (data) =>
      rewriteHomePages(data, (candidate) =>
        candidate.post_id === post.post_id ? post : candidate,
      ),
  );
}

/** Remove a server-confirmed deletion from every already-loaded Feed page. */
export function removeConfirmedFeedPost(
  queryClient: QueryClient,
  postId: string,
): void {
  for (const query of queryClient.getQueryCache().findAll({
    queryKey: feedQueryKeys.all,
  })) {
    bumpAuthorityRevision(query);
  }
  for (const query of queryClient.getQueryCache().findAll({
    queryKey: homeFeedQueryKeys.all,
  })) {
    bumpAuthorityRevision(query);
  }
  const pinState = confirmedPinStates.get(queryClient);
  if (pinState) {
    for (const [queryHash, pinned] of pinState.queries) {
      pinned.posts.delete(postId);
      if (pinned.posts.size === 0) pinState.queries.delete(queryHash);
    }
    releasePinStateIfEmpty(queryClient, pinState);
  }
  queryClient.setQueriesData<InfiniteData<FeedPage>>(
    { queryKey: feedQueryKeys.all },
    (data) =>
      rewritePages(data, (candidate) =>
        candidate.post_id === postId ? null : candidate,
      ),
  );
  removeConfirmedHomePostPin(queryClient, postId);
  queryClient.setQueriesData<InfiniteData<HomeFeedPage>>(
    { queryKey: homeFeedQueryKeys.all },
    (data) =>
      rewriteHomePages(data, (candidate) =>
        candidate.post_id === postId ? null : candidate,
      ),
  );
}

export function invalidateFeedQueries(
  queryClient: QueryClient,
  targets?: readonly FeedCacheTarget[],
): void {
  for (const query of queryClient.getQueryCache().findAll({
    queryKey: feedQueryKeys.all,
    predicate: targets ? (query) => matchesTargets(query, targets) : undefined,
  })) {
    bumpAuthorityRevision(query);
  }
  if (!targets || targets.some((target) => target.scope === "home")) {
    for (const query of queryClient.getQueryCache().findAll({
      queryKey: homeFeedQueryKeys.all,
    })) {
      bumpAuthorityRevision(query);
    }
  }
  void queryClient.invalidateQueries({
    queryKey: feedQueryKeys.all,
    predicate: targets ? (query) => matchesTargets(query, targets) : undefined,
  });
  if (!targets || targets.some((target) => target.scope === "home")) {
    void queryClient.invalidateQueries({ queryKey: homeFeedQueryKeys.all });
  }
}

const RECONCILIATION_DELAYS_MS = [1_250, 2_500, 5_000] as const;
const pendingReconciliations = new WeakMap<
  QueryClient,
  Map<string, Array<ReturnType<typeof setTimeout>>>
>();
const reconciliationGenerations = new WeakMap<QueryClient, number>();

/**
 * Capture in onMutate and compare before any confirmed-result cache writes.
 * The existing QueryCacheAuthBoundary advances this epoch on identity changes;
 * ordinary navigation does not invalidate a same-identity completion.
 */
export function getFeedIdentityGeneration(queryClient: QueryClient): number {
  return reconciliationGenerations.get(queryClient) ?? 0;
}

/** Cancel Feed work before one QueryClient begins serving another identity. */
export function resetFeedCacheForIdentityChange(
  queryClient: QueryClient,
): void {
  resetFeedFreshnessTelemetry();
  reconciliationGenerations.set(
    queryClient,
    (reconciliationGenerations.get(queryClient) ?? 0) + 1,
  );
  const pending = pendingReconciliations.get(queryClient);
  for (const timers of pending?.values() ?? []) {
    for (const timer of timers) clearTimeout(timer);
  }
  pending?.clear();
  pendingReconciliations.delete(queryClient);

  const pins = confirmedPinStates.get(queryClient);
  if (pins) {
    pins.unsubscribe();
    pins.queries.clear();
    confirmedPinStates.delete(queryClient);
  }
  resetConfirmedHomePins(queryClient);
}

/**
 * Audience projections are asynchronous. Recheck only active matching feeds at
 * three bounded points; repeated actions coalesce and never create polling.
 */
export function scheduleFeedReconciliation(
  queryClient: QueryClient,
  targets: readonly FeedCacheTarget[],
): void {
  for (const query of queryClient.getQueryCache().findAll({
    queryKey: feedQueryKeys.all,
    predicate: (query) => matchesTargets(query, targets),
  })) {
    bumpAuthorityRevision(query);
  }
  if (targets.some((target) => target.scope === "home")) {
    for (const query of queryClient.getQueryCache().findAll({
      queryKey: homeFeedQueryKeys.all,
    })) {
      bumpAuthorityRevision(query);
    }
  }
  const signature = JSON.stringify(
    [...targets]
      .map((target) => [target.scope, target.subject ?? null])
      .sort(([scopeA, subjectA], [scopeB, subjectB]) =>
        `${scopeA}:${subjectA}`.localeCompare(`${scopeB}:${subjectB}`),
      ),
  );
  const clientPending = pendingReconciliations.get(queryClient) ?? new Map();
  pendingReconciliations.set(queryClient, clientPending);
  for (const timer of clientPending.get(signature) ?? []) clearTimeout(timer);
  const generation = reconciliationGenerations.get(queryClient) ?? 0;

  const timers = RECONCILIATION_DELAYS_MS.map((delay, index) =>
    setTimeout(() => {
      if ((reconciliationGenerations.get(queryClient) ?? 0) !== generation) {
        return;
      }
      void queryClient.refetchQueries({
        queryKey: feedQueryKeys.all,
        predicate: (query) => matchesTargets(query, targets),
        type: "active",
      });
      if (targets.some((target) => target.scope === "home")) {
        void queryClient.refetchQueries({
          queryKey: homeFeedQueryKeys.all,
          type: "active",
        });
      }
      if (index === RECONCILIATION_DELAYS_MS.length - 1) {
        clientPending.delete(signature);
      }
    }, delay),
  );
  clientPending.set(signature, timers);
}
